import React, { useState, useEffect } from 'react';
import { MessageSquare, User, Send, Reply, ThumbsUp, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CommentForm = ({ postId, parentId = null, onSubmit, onCancel, placeholder = "Share your thoughts..." }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !content.trim()) return;

    setSubmitting(true);
    setStatus(null);

    try {
      const response = await axios.post(`${API}/comments/posts/${postId}/comments`, {
        post_id: postId,
        author_name: name,
        author_email: email,
        content: content,
        parent_id: parentId
      });

      if (response.data.success) {
        setStatus('success');
        setContent('');
        if (!parentId) {
          setName('');
          setEmail('');
        }
        if (onSubmit) onSubmit();
      }
    } catch (error) {
      setStatus('error');
      console.error('Error submitting comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!parentId && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
            required
            data-testid="comment-name-input"
          />
          <Input
            type="email"
            placeholder="Your email (not published)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
            required
            data-testid="comment-email-input"
          />
        </div>
      )}
      <Textarea
        placeholder={placeholder}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 min-h-[100px]"
        required
        data-testid="comment-content-input"
      />
      
      {status === 'success' && (
        <div className="flex items-center gap-2 text-green-400 text-sm">
          <CheckCircle className="h-4 w-4" />
          Comment submitted! It will appear after moderation.
        </div>
      )}
      
      {status === 'error' && (
        <div className="flex items-center gap-2 text-red-400 text-sm">
          <AlertCircle className="h-4 w-4" />
          Failed to submit comment. Please try again.
        </div>
      )}

      <div className="flex gap-2">
        <Button 
          type="submit" 
          disabled={submitting || !name.trim() || !email.trim() || !content.trim()}
          className="btn-primary"
          data-testid="comment-submit-btn"
        >
          {submitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Submitting...
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              {parentId ? 'Post Reply' : 'Post Comment'}
            </>
          )}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} className="border-gray-700 text-gray-300">
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
};

const SingleComment = ({ comment, postId, onReplySubmit }) => {
  const [showReplyForm, setShowReplyForm] = useState(false);

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="border-l-2 border-gray-700 pl-4 py-3" data-testid={`comment-${comment.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-orange-500/20 flex items-center justify-center">
          <User className="h-5 w-5 text-orange-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-white">{comment.author_name}</span>
            <span className="text-gray-500 text-sm flex items-center">
              <Clock className="h-3 w-3 mr-1" />
              {formatDate(comment.created_at)}
            </span>
          </div>
          <p className="text-gray-300 mt-2 whitespace-pre-wrap">{comment.content}</p>
          
          <div className="flex items-center gap-4 mt-3">
            <button 
              onClick={() => setShowReplyForm(!showReplyForm)}
              className="text-sm text-gray-400 hover:text-orange-400 flex items-center gap-1 transition-colors"
              data-testid={`reply-btn-${comment.id}`}
            >
              <Reply className="h-4 w-4" />
              Reply
            </button>
          </div>

          {showReplyForm && (
            <div className="mt-4">
              <CommentForm 
                postId={postId}
                parentId={comment.id}
                placeholder={`Reply to ${comment.author_name}...`}
                onSubmit={() => {
                  setShowReplyForm(false);
                  if (onReplySubmit) onReplySubmit();
                }}
                onCancel={() => setShowReplyForm(false)}
              />
            </div>
          )}

          {/* Nested Replies */}
          {comment.replies && comment.replies.length > 0 && (
            <div className="mt-4 space-y-3">
              {comment.replies.map((reply) => (
                <div key={reply.id} className="border-l-2 border-gray-800 pl-4 py-2">
                  <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-700 flex items-center justify-center">
                      <User className="h-4 w-4 text-gray-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white text-sm">{reply.author_name}</span>
                        <span className="text-gray-500 text-xs">{formatDate(reply.created_at)}</span>
                      </div>
                      <p className="text-gray-300 text-sm mt-1">{reply.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CommentsSection = ({ postId }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/comments/posts/${postId}/comments`);
      setComments(response.data.comments || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [postId]);

  return (
    <Card className="bg-gray-900 border-gray-800" data-testid="comments-section">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-orange-400" />
          Comments ({comments.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Comment Form */}
        <div className="pb-6 border-b border-gray-800">
          <h3 className="text-lg font-medium text-white mb-4">Leave a Comment</h3>
          <CommentForm postId={postId} onSubmit={fetchComments} />
        </div>

        {/* Comments List */}
        <div className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : error ? (
            <p className="text-red-400 text-center py-4">{error}</p>
          ) : comments.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="h-12 w-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No comments yet. Be the first to share your thoughts!</p>
            </div>
          ) : (
            comments.map((comment) => (
              <SingleComment 
                key={comment.id} 
                comment={comment} 
                postId={postId}
                onReplySubmit={fetchComments}
              />
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CommentsSection;
